/**
 * Theme Variables for Inline Styles
 *
 * This object provides CSS variable references in a theme-like structure
 * for use in inline styles. Unlike the Material-UI theme object, these
 * return CSS variable strings that are evaluated dynamically by the browser.
 *
 * Usage:
 *   import themeVars from 'theme/themeVars'
 *   const styles = { backgroundColor: themeVars.palette.primary1Color }
 */

const themeVars = {
  palette: {
    // Primary colors
    primary1Color: 'var(--cg-color-primary-main)',
    primary1HoverColor: 'var(--cg-color-primary-hover)',
    primary1LightColor: 'var(--cg-color-primary-light)',
    primary1HoverBackgroundColor: 'var(--cg-color-primary-hover-bg)',

    accent1Color: 'var(--cg-color-accent)',

    // Text colors
    primaryFontColor: 'var(--cg-color-text-primary)',
    darkTextColor: 'var(--cg-color-text-primary)',
    lightTextColor: 'var(--cg-color-text-light)',
    disabledColor: 'var(--cg-color-text-disabled)',
    disabledTextColor: 'var(--cg-color-text-disabled)',

    // Icon colors
    primaryIconColor: 'var(--cg-color-icon-primary)',
    lightIconColor: 'var(--cg-color-icon-light)',
    disabledIconColor: 'var(--cg-color-icon-disabled)',

    // Background colors
    whiteColor: 'var(--cg-color-bg-white)',
    background1Color: 'var(--cg-color-bg-default)',
    background2Color: 'var(--cg-color-bg-level2)',
    background3Color: 'var(--cg-color-bg-level3)',
    background4Color: 'var(--cg-color-bg-level4)',
    disabledBackgroundColor: 'var(--cg-color-bg-disabled)',
    hoverBackgroundColor: 'var(--cg-color-bg-hover-light)',

    // Border colors
    borderColor: 'var(--cg-color-border-default)',
    lightBorderColor: 'var(--cg-color-border-light)',

    // State colors
    errorColor: 'var(--cg-color-error)',
    warningColor: 'var(--cg-color-warning)',
    goodColor: 'var(--cg-color-success)',

    // Contrast colors (for dark header/sidebar)
    contrastBackgroundColor: 'var(--cg-color-contrast-bg)',
    contrastBorderColor: 'var(--cg-color-contrast-border)',
    contrastTextColor: 'var(--cg-color-contrast-text)',
    contrastIconColor: 'var(--cg-color-contrast-icon)',
    contrastHoverBackgroundColor: 'var(--cg-color-contrast-hover-bg)',

    // Header colors
    headerColor: 'var(--cg-color-header-bg)',
    hoverHighlightColor: 'var(--cg-color-header-hover)',
    blackColor: 'var(--cg-color-header-text-active)',

    // Delivery status
    deliveryStatus: {
      unscheduled: 'var(--cg-color-delivery-unscheduled)',
      scheduled: 'var(--cg-color-delivery-scheduled)',
      wip: 'var(--cg-color-delivery-wip)',
      onHold: 'var(--cg-color-delivery-onhold)',
      complete: 'var(--cg-color-delivery-complete)',
      note: 'var(--cg-color-delivery-note)',
    },

    // Job board status
    jobCardStatus: {
      pending: 'var(--cg-color-jobboard-pending)',
      wip: 'var(--cg-color-jobboard-wip)',
      onHold: 'var(--cg-color-jobboard-onhold)',
      complete: 'var(--cg-color-jobboard-complete)',
      notScheduled: 'var(--cg-color-jobboard-not-scheduled)',
      noProductionRequired: 'var(--cg-color-jobboard-no-production)',
      delivered: 'var(--cg-color-jobboard-delivered)',
    },

    // Quote/Order status
    quoteStatus: {
      draft: 'var(--cg-color-quote-draft)',
      quoted: 'var(--cg-color-quote-quoted)',
      accepted: 'var(--cg-color-quote-accepted)',
      confirmed: 'var(--cg-color-quote-confirmed)',
      partDelivered: 'var(--cg-color-quote-part-delivered)',
      delivered: 'var(--cg-color-quote-delivered)',
      inDelivery: 'var(--cg-color-quote-in-delivery)',
      invoiced: 'var(--cg-color-quote-invoiced)',
      credited: 'var(--cg-color-quote-credited)',
      paid: 'var(--cg-color-quote-paid)',
      closed: 'var(--cg-color-quote-closed)',
      rejected: 'var(--cg-color-quote-rejected)',
      noSchedule: 'var(--cg-color-quote-no-schedule)',
    },

    // Activity status
    activityStatus: {
      pending: 'var(--cg-color-activity-status-pending)',
      inProgress: 'var(--cg-color-activity-status-in-progress)',
      onHold: 'var(--cg-color-activity-status-on-hold)',
      rejected: 'var(--cg-color-activity-status-rejected)',
      completed: 'var(--cg-color-activity-status-completed)',
    },

    // Contact types
    contactType: {
      general: 'var(--cg-color-contact-type-general)',
      main: 'var(--cg-color-contact-type-main)',
      dormant: 'var(--cg-color-contact-type-dormant)',
      accounts: 'var(--cg-color-contact-type-accounts)',
    },

    // Invoice type colors
    invoiceType: {
      awaitingPosting: 'var(--cg-color-invoice-awaiting-posting)',
      invoice: 'var(--cg-color-invoice-invoice)',
      creditNote: 'var(--cg-color-invoice-credit-note)',
    },

    // Boolean field colors
    booleanField: {
      false: 'var(--cg-color-boolean-false)',
      true: 'var(--cg-color-boolean-true)',
    },

    // Part types
    partType: {
      material: 'var(--cg-color-part-type-material)',
      labour: 'var(--cg-color-part-type-labour)',
      resource: 'var(--cg-color-part-type-resource)',
      outsourced: 'var(--cg-color-part-type-outsourced)',
      nonStock: 'var(--cg-color-part-type-non-stock)',
      timesheet: 'var(--cg-color-part-type-timesheet)',
      expenses: 'var(--cg-color-part-type-expenses)',
      assembly: 'var(--cg-color-part-type-assembly)',
      calcWizard: 'var(--cg-color-part-type-calc-wizard)',
      rental: 'var(--cg-color-part-type-rental)',
      service: 'var(--cg-color-part-type-service)',
    },

    // Status colors
    overdue: 'var(--cg-color-status-overdue)',

    // Pro brand colors
    proPurple: 'var(--cg-color-brand-purple)',
    proPurpleBackground: 'var(--cg-color-brand-purple-bg)',
    proPink: 'var(--cg-color-brand-pink)',
    proPinkBackground: 'var(--cg-color-brand-pink-bg)',

    // State background colors
    goodBackgroundColor: 'var(--cg-color-success-bg)',
    goodBackground2Color: 'var(--cg-color-success-bg2)',
    infoColor: 'var(--cg-color-info)',
    infoBackgroundColor: 'var(--cg-color-info-bg)',
    infoBackground2Color: 'var(--cg-color-info-bg2)',
    errorBackgroundColor: 'var(--cg-color-error-bg)',
    errorBackground2Color: 'var(--cg-color-error-bg2)',
    warningBackgroundColor: 'var(--cg-color-warning-bg)',
    warningBorderColor: 'var(--cg-color-warning-border)',

    // Utility colors
    redColor: 'var(--cg-color-error-bright)',
    signatureColor: 'var(--cg-color-signature)',

    // Contrast colors - extended
    contrastHoverTextColor: 'var(--cg-color-contrast-hover-text)',
    contrastActiveBackgroundColor: 'var(--cg-color-contrast-active-bg)',
    contrastActiveTextColor: 'var(--cg-color-contrast-active-text)',
    contrastActiveIconColor: 'var(--cg-color-contrast-active-icon)',

    // Contacts object
    contacts: {
      postedStatus: {
        notPosted: 'var(--cg-color-contact-posted-not-posted)',
        posted: 'var(--cg-color-contact-posted-posted)',
        failedToPost: 'var(--cg-color-contact-posted-failed)',
      },
      emailSendStatus: {
        notSent: 'var(--cg-color-contact-email-not-sent)',
        ok: 'var(--cg-color-contact-email-ok)',
        softBounce: 'var(--cg-color-contact-email-soft-bounce)',
        hardBounce: 'var(--cg-color-contact-email-hard-bounce)',
      },
      emailValidation: {
        valid: 'var(--cg-color-contact-email-valid)',
        invalid: 'var(--cg-color-contact-email-invalid)',
      },
    },

    // Delivery note status
    deliveryNoteStatus: {
      draft: 'var(--cg-color-delivery-note-draft)',
      draftReturn: 'var(--cg-color-delivery-note-draft-return)',
      awaitingReturn: 'var(--cg-color-delivery-note-awaiting-return)',
      returned: 'var(--cg-color-delivery-note-returned)',
      scheduled: 'var(--cg-color-delivery-note-scheduled)',
      delivered: 'var(--cg-color-delivery-note-delivered)',
      collected: 'var(--cg-color-delivery-note-collected)',
    },

    // Delivery item status
    deliveryItemStatus: {
      pending: 'var(--cg-color-delivery-item-pending)',
      readyForDelivery: 'var(--cg-color-delivery-item-ready)',
      scheduled: 'var(--cg-color-delivery-item-scheduled)',
      delivered: 'var(--cg-color-delivery-item-delivered)',
    },

    // Opportunity status
    opportunityStatus: {
      rejected: 'var(--cg-color-opportunity-rejected)',
      won: 'var(--cg-color-opportunity-won-alt)',
      open: 'var(--cg-color-opportunity-open)',
    },

    // Production status
    productionStatus: {
      unscheduled: 'var(--cg-color-production-unscheduled)',
      scheduled: 'var(--cg-color-production-scheduled)',
      wip: 'var(--cg-color-production-wip)',
      onHold: 'var(--cg-color-production-on-hold)',
      complete: 'var(--cg-color-production-complete)',
    },

    // Approval status
    approvalStatus: {
      approved: 'var(--cg-color-approval-approved)',
      changesRequested: 'var(--cg-color-approval-changes-requested)',
      draft: 'var(--cg-color-approval-draft)',
      inReview: 'var(--cg-color-approval-in-review)',
      rejected: 'var(--cg-color-approval-rejected)',
    },

    // Purchase order status
    purchaseOrderStatus: {
      draft: 'var(--cg-color-purchase-order-draft)',
      issued: 'var(--cg-color-purchase-order-issued)',
      partReceived: 'var(--cg-color-purchase-order-part-received)',
      received: 'var(--cg-color-purchase-order-received)',
      closed: 'var(--cg-color-purchase-order-closed)',
      cancelled: 'var(--cg-color-purchase-order-cancelled)',
      confirmed: 'var(--cg-color-purchase-order-confirmed)',
      paid: 'var(--cg-color-purchase-order-paid)',
    },

    // Simple status colors
    pendingStatusColor: 'var(--cg-color-status-pending)',
    inProgressStatusColor: 'var(--cg-color-status-in-progress)',
    onHoldStatusColor: 'var(--cg-color-status-on-hold)',
    rejectStatusColor: 'var(--cg-color-status-rejected)',
    completedStatusColor: 'var(--cg-color-status-completed)',
  },

  // Part status (outside palette, matching theme/index.js structure)
  partStatus: {
    active: 'var(--cg-color-part-active)',
    buyOnly: 'var(--cg-color-part-buy-only)',
    comingSoon: 'var(--cg-color-part-coming-soon)',
    discontinued: 'var(--cg-color-part-discontinued)',
  },

  type: {
    title: {
      fontFamily: 'var(--cg-font-family-primary)',
      fontWeight: 'var(--cg-font-weight-medium)',
      color: 'var(--cg-color-text-primary)',
    },
  },
}

export default themeVars
